<template>
  <v-container fluid class="car-page">
    <a-row class="car-row">
      <!-- ==================== 左侧：车牌列表（仅自有车 / 包车，带搜索） ==================== -->
      <a-col :span="3" class="left-col">
        <v-card tile class="left-card" :loading="vehicleLoading">
          <div class="panel-header">
            <v-text-field v-model="keyword" label="车牌号" outlined dense clearable hide-details
              prepend-inner-icon="mdi-magnify" placeholder="搜索车牌号" class="search-input" @keyup.enter="getVehicleList"
              @click:clear="getVehicleList" />
          </div>
          <div class="vehicle-list">
            <div v-for="item in vehicles" :key="item._id" class="vehicle-item"
              :class="{ active: current && current._id === item._id }" @click="selectVehicle(item)">
              <div class="vehicle-no">{{ item.vehicle_number }}</div>
              <div class="vehicle-meta">
                <span class="v-type">{{ item.vehicle_type || '-' }}</span>
                <span class="v-own" :class="item.own_type === 1 ? 'own-self' : 'own-charter'">
                  {{ item.own_type === 1 ? '自有车' : '包车' }}
                </span>
              </div>
            </div>
            <div v-if="!vehicleLoading && !vehicles.length" class="empty-tip">暂无车辆</div>
          </div>
          <div class="panel-footer">共 {{ vehicles.length }} 辆（自有车 / 包车）</div>
        </v-card>
      </a-col>
      <!-- ==================== 右侧：上方地图轨迹 + 下方轨迹明细 ==================== -->
      <a-col :span="21" class="right-col">
        <div class="right-wrap">
          <!-- 顶部工具栏：日期范围 + 查询 + 当前车辆 -->
          <v-card tile class="toolbar-card">
            <div class="d-flex align-center flex-wrap">
              <v-menu ref="start_day_menu" v-model="start_day_menu" :close-on-content-click="false"
                :return-value.sync="start_day" transition="scale-transition" min-width="auto" offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="start_day" label="开始日期" append-icon="mdi-calendar" readonly v-bind="attrs"
                    v-on="on" dense outlined hide-details class="date-field">
                  </v-text-field>
                </template>
                <v-date-picker v-model="start_day" no-title value="YYYY-MM-DD" scrollable locale="zh-cn"
                  @input="() => { $refs.start_day_menu.save(start_day); start_day_menu = false; }"></v-date-picker>
              </v-menu>
              <span class="date-sep">至</span>
              <v-menu ref="end_day_menu" v-model="end_day_menu" :close-on-content-click="false"
                :return-value.sync="end_day" transition="scale-transition" min-width="auto" offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="end_day" label="结束日期" append-icon="mdi-calendar" readonly v-bind="attrs"
                    v-on="on" dense outlined hide-details class="date-field">
                  </v-text-field>
                </template>
                <v-date-picker v-model="end_day" no-title value="YYYY-MM-DD" scrollable locale="zh-cn"
                  @input="() => { $refs.end_day_menu.save(end_day); end_day_menu = false; }"></v-date-picker>
              </v-menu>
              <v-btn color="primary" dark tile class="ml-2" :disabled="!current" @click="reloadTrack">
                <v-icon small>mdi-magnify</v-icon>查询
              </v-btn>
              <v-spacer></v-spacer>
              <span v-if="trackPoints.length" class="track-legend">
                <v-icon x-small color="#1976d2" class="legend-icon">mdi-record</v-icon>途径点
                <v-icon x-small color="#00b42a" class="legend-icon ml-2">mdi-flag</v-icon>起点
                <v-icon x-small color="#f53f3f" class="legend-icon ml-2">mdi-map-marker</v-icon>当前位置
                <em class="ml-2">共 {{ trackPoints.length }} 个定位点</em>
              </span>
              <span class="current-vehicle ml-3">
                <v-icon small>mdi-truck</v-icon>{{ current ? current.vehicle_number : '未选择车辆' }}
              </span>
            </div>
          </v-card>

          <!-- 地图：展示轨迹 -->
          <div class="map-card">
            <div id="carTrackMap" class="map-box"></div>
            <div v-if="!current" class="map-mask">
              <v-icon color="grey lighten-1" large>mdi-map-marker-alert</v-icon>
              <p>请从左侧选择车辆查看运行轨迹</p>
            </div>
            <div v-else-if="trackLoading" class="map-mask">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <p class="mt-2">轨迹加载中...</p>
            </div>
            <!-- 明细折叠后的展开入口（样式与 map.vue 统一） -->
            <div v-if="trackCollapsed" class="list-expand-btn" @click="toggleTrack(false)">
              <v-icon size="16" color="primary">mdi-chevron-down</v-icon>
              <span>展开明细</span>
            </div>
          </div>

          <!-- 下方：任务轨迹（默认）/ 轨迹明细，可折叠，折叠后地图占满 -->
          <v-card v-show="!trackCollapsed" tile class="track-card">
            <div class="track-title">
              <v-btn text small class="collapse-btn" @click="toggleTrack(true)">
                <v-icon small left>mdi-chevron-up</v-icon>折叠
              </v-btn>
              <a-tabs v-model="bottomTab" size="small" class="bottom-tabs" @change="onBottomTabChange">
                <a-tab-pane key="task" tab="任务轨迹"></a-tab-pane>
                <a-tab-pane key="track" tab="轨迹明细"></a-tab-pane>
              </a-tabs>
              <v-spacer></v-spacer>
            </div>

            <!-- 任务轨迹：任务列表 / 单任务流程图 -->
            <div v-show="bottomTab === 'task'" class="task-pane">
              <!-- 任务列表（一天可能有多个任务，先选任务再看轨迹） -->
              <template v-if="!activeTask">
                <a-table size="small" :columns="taskHeaders" :data-source="taskList" :pagination="false"
                  :locale="{ emptyText: '当前日期范围内暂无任务路单' }" :scroll="{ y: 190 }" row-key="_id"
                  :loading="taskLoading" :custom-row="taskRow">
                  <template slot="index" slot-scope="text, record, index">
                    {{ index + 1 }}
                  </template>
                  <template slot="own_type" slot-scope="text">
                    <span class="v-own" :class="text === 1 ? 'own-self' : 'own-charter'">
                      {{ text === 1 ? '自有车' : '包车' }}
                    </span>
                  </template>
                  <template slot="action" slot-scope="text, record">
                    <v-btn text small color="primary" @click.stop="showTaskTrack(record)">轨迹</v-btn>
                  </template>
                </a-table>
              </template>
              <!-- 单任务流程图 -->
              <template v-else>
                <div class="task-flow-head">
                  <v-btn text small color="primary" class="back-btn" @click="backToTaskList">
                    <v-icon small left>mdi-arrow-left</v-icon>返回任务列表
                  </v-btn>
                  <span class="task-flow-title">
                    派车单 {{ activeTask.dispatch_no }}
                    <em>{{ activeTask.date }}</em>
                    <em>{{ activeTask.line }}</em>
                    <em>{{ activeTask.vehicle_number }}</em>
                  </span>
                </div>
                <div class="task-flow">
                  <!-- 第一行：节点 0-5（左起右排），a-steps 步骤条 -->
                  <div class="steps-row">
                    <a-steps label-placement="vertical" size="small">
                      <a-step v-for="node in flowRow1" :key="node.key" :status="node.time ? 'finish' : 'wait'">
                        <template slot="icon">
                          <div class="step-dot" :class="{ done: !!node.time }">
                            <template v-if="node.time">
                              <span>{{ node.time | flowDate }}</span>
                              <span>{{ node.time | flowTime }}</span>
                            </template>
                            <span v-else class="node-empty">--</span>
                          </div>
                        </template>
                        <template slot="title">{{ node.name }}</template>
                      </a-step>
                    </a-steps>
                    <!-- 连线耗时：绝对定位在两节点连线中点上方 -->
                    <span v-for="(d, i) in row1Durations" :key="'d1' + i" v-show="d" class="line-duration"
                      :style="{ left: ((i + 1) / 6 * 100) + '%' }">{{ d }}</span>
                  </div>
                  <!-- 跨行向下竖线：右端对齐行尾节点圆心 -->
                  <div class="turn-wrap">
                    <span class="turn-line" :class="turnCls"></span>
                    <em v-if="turnDuration">{{ turnDuration }}</em>
                  </div>
                  <!-- 第二行：节点 6-10（视觉左→右倒序），宽 5/6 右对齐，出围栏与封车垂直对齐 -->
                  <div class="steps-row reverse-row">
                    <a-steps label-placement="vertical" size="small" :style="{ width: (5 / 6 * 100) + '%' }">
                      <a-step v-for="node in flowRow2" :key="node.key" :status="node.time ? 'finish' : 'wait'">
                        <template slot="icon">
                          <div class="step-dot" :class="{ done: !!node.time }">
                            <template v-if="node.time">
                              <span>{{ node.time | flowDate }}</span>
                              <span>{{ node.time | flowTime }}</span>
                            </template>
                            <span v-else class="node-empty">--</span>
                          </div>
                        </template>
                        <template slot="title">{{ node.name }}</template>
                      </a-step>
                    </a-steps>
                    <span v-for="(d, j) in row2Durations" :key="'d2' + j" v-show="d" class="line-duration"
                      :style="{ left: ((j + 2) / 6 * 100) + '%' }">{{ d }}</span>
                  </div>
                </div>
              </template>
            </div>

            <!-- 轨迹明细：轨迹点表格 -->
            <div v-show="bottomTab === 'track'">
              <a-table size="small" :columns="trackHeaders" :data-source="trackPoints" :pagination="false"
                :locale="{ emptyText: '暂无轨迹数据' }" :scroll="{ y: 200 }" row-key="_id" :loading="trackLoading">
                <template slot="index" slot-scope="text, record, index">
                  {{ index + 1 }}
                </template>
                <template slot="speed" slot-scope="text">
                  {{ text }} KM/H
                </template>
              </a-table>
            </div>
          </v-card>
        </div>
      </a-col>
    </a-row>
  </v-container>
</template>

<script>
import { Scene, PointLayer, LineLayer, GaodeMap } from '@antv/l7';
// 模拟接口数据源：后端接口就绪后删除此引入，直接调用 this.$api.doAPI
import mockJson from './data.json';

/* ============================================================================
 * 待对接的后端接口地址（接口写好后，把 methods 里对应的 mockXxx() 调用，
 * 直接替换成 this.$api.doAPI(URL, params) 即可，返回结构均约定为
 * { code: 0, data: { list: [...] } }，页面其余逻辑无需改动）。
 * ========================================================================== */
const API_VEHICLE_LIST = "/vehiclecar/list"; // 左侧车辆列表（仅自有车/包车，支持 text 车牌搜索）
const API_VEHICLE_TRACK = "/vehiclecar/track";      // TODO 车辆轨迹点，最终以后端实际地址为准
const API_VEHICLE_TASKLIST = "/vehiclecar/tasklist";  // TODO 任务路单列表，最终以后端实际地址为准
const API_VEHICLE_TASKTRACK = "/vehiclecar/tasktrack"; // TODO 任务轨迹节点，最终以后端实际地址为准

// ==================== 任务轨迹：节点定义（顺序即流程顺序） ====================
const TASK_NODE_DEFS = [
  { key: "create", name: "生成派车单" },
  { key: "response", name: "派车单响应" },
  { key: "arrive", name: "车辆到场" },
  { key: "load_start", name: "开始装车" },
  { key: "load_end", name: "结束装车" },
  { key: "seal", name: "封车(路单)" },
  { key: "out_fence", name: "出围栏" },
  { key: "in_fence", name: "进围栏" },
  { key: "enter", name: "车辆入局" },
  { key: "unload_start", name: "解车开始" },
  { key: "unload_end", name: "解车结束" },
];
const FLOW_PER_ROW = 6; // 流程图每行节点数（两行蛇形）

// ==================== 以下为前端模拟接口（数据源 data.json，后端接口就绪后可整段删除） ====================

// 地图自定义点位图标（SVG data URI，与右上角图例一致）
const START_ICON = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
    <circle cx="16" cy="16" r="13.5" fill="#00b42a" stroke="#ffffff" stroke-width="2.5"/>
    <path transform="translate(4,4)" fill="#ffffff" d="M14.4,6H20V16H13L12.6,14H7v7H5V4h9L14.4,6z"/>
  </svg>`
);
const END_ICON = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path fill="#f53f3f" stroke="#ffffff" stroke-width="1.2"
      d="M12,2C8.13,2 5,5.13 5,9c0,5.25 7,13 7,13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0,9.5A2.5,2.5 0 1,1 12,6.5a2.5,2.5 0 0,1 0,5z"/>
  </svg>`
);

export default {
  name: "VehicleCar",
  data() {
    return {
      // 左侧车辆列表
      keyword: "",
      vehicles: [],
      vehicleLoading: false,
      current: null,
      // 日期范围
      start_day: "",
      end_day: "",
      start_day_menu: false,
      end_day_menu: false,
      // 地图相关
      mapScene: null,
      mapReady: false,
      lineLayer: null,
      pointLayer: null,
      nameLayer: null,
      startNameLayer: null,
      endNameLayer: null,
      startLayer: null,
      endLayer: null,
      // 轨迹点
      trackLoading: false,
      trackPoints: [],
      trackCollapsed: false, // 轨迹明细折叠状态
      trackHeaders: [
        { title: "序号", dataIndex: "index", scopedSlots: { customRender: "index" }, width: 60, align: "center" },
        { title: "定位时间", dataIndex: "time", width: 170, align: "center" },
        { title: "位置", dataIndex: "address" },
        { title: "速度", dataIndex: "speed", scopedSlots: { customRender: "speed" }, width: 110, align: "center" },
        { title: "经度", dataIndex: "longitude", width: 110, align: "center" },
        { title: "纬度", dataIndex: "latitude", width: 110, align: "center" },
      ],
      // 任务轨迹
      bottomTab: "task", // 底部面板 tab：task 任务轨迹 / track 轨迹明细
      taskLoading: false,
      taskList: [], // 当前车辆+日期范围内的任务路单
      activeTask: null, // 选中的任务（显示流程图），null 时显示任务列表
      taskHeaders: [
        { title: "序号", dataIndex: "index", scopedSlots: { customRender: "index" }, width: 55, align: "center" },
        { title: "派车单号", dataIndex: "dispatch_no", width: 130, align: "center" },
        { title: "日期", dataIndex: "date", width: 110, align: "center" },
        { title: "线路", dataIndex: "line" },
        { title: "车牌号", dataIndex: "vehicle_number", width: 110, align: "center" },
        { title: "挂箱号", dataIndex: "trailer_number", width: 110, align: "center" },
        { title: "车辆属性", dataIndex: "own_type", scopedSlots: { customRender: "own_type" }, width: 90, align: "center" },
        { title: "操作", dataIndex: "action", scopedSlots: { customRender: "action" }, width: 80, align: "center" },
      ],
    };
  },
  filters: {
    // 流程图节点时间拆分：日期 MM-DD / 时间 HH:mm
    flowDate(v) {
      return v ? v.slice(5, 10) : "--";
    },
    flowTime(v) {
      return v ? v.slice(11, 16) : "--";
    },
  },
  computed: {
    // 任务节点（11 个，注入时间）
    taskNodes() {
      if (!this.activeTask) return [];
      return TASK_NODE_DEFS.map((d) => ({
        ...d,
        time: this.activeTask.nodes?.[d.key] || null,
      }));
    },
    // 第一行：节点 0-5
    flowRow1() {
      return this.taskNodes.slice(0, FLOW_PER_ROW);
    },
    // 第二行：节点 6-10，视觉左→右倒序（解车结束…出围栏），右对齐
    flowRow2() {
      return this.taskNodes.slice(FLOW_PER_ROW).reverse();
    },
    // 第一行连线耗时：i 为连线左端节点索引
    row1Durations() {
      const n = this.taskNodes;
      return n.slice(0, FLOW_PER_ROW - 1).map((node, i) => this.flowDuration(node.time, n[i + 1].time));
    },
    // 第二行连线耗时：视觉索引 j(0..3) → 实际节点 9-j、10-j（流向 9-j → 10-j）
    row2Durations() {
      const n = this.taskNodes;
      const last = n.length - 1;
      return n.slice(FLOW_PER_ROW, last).map((_, j) => this.flowDuration(n[last - 1 - j].time, n[last - j].time));
    },
    // 跨行耗时：封车(5) → 出围栏(6)
    turnDuration() {
      const n = this.taskNodes;
      return n.length > FLOW_PER_ROW ? this.flowDuration(n[FLOW_PER_ROW - 1].time, n[FLOW_PER_ROW].time) : null;
    },
    turnCls() {
      const n = this.taskNodes;
      if (n.length <= FLOW_PER_ROW || !n[FLOW_PER_ROW - 1].time) return "empty";
      return n[FLOW_PER_ROW].time ? "active" : "empty";
    },
  },
  mounted() {
    // 默认查询前一天
    const d = this.$moment().subtract(0, "day").format("YYYY-MM-DD");
    this.start_day = d;
    this.end_day = d;
    this.initMap();
    window.addEventListener("resize", this.onResize);
  },
  activated() {
    // keep-alive 再次进入：刷新车辆列表、校正地图尺寸
    this.getVehicleList();
    this.$nextTick(() => {
      this.mapScene && this.mapScene.map && this.mapScene.map.resize();
    });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    if (this.mapScene) {
      this.mapScene.destroy();
      this.mapScene = null;
    }
  },
  methods: {
    // 窗口变化时让地图重新计算尺寸
    onResize() {
      this.$nextTick(() => {
        this.mapScene && this.mapScene.map && this.mapScene.map.resize();
      });
    },

    // 折叠 / 展开轨迹明细，切换后等待布局过渡再重算地图尺寸（与 map.vue 一致）
    toggleTrack(collapsed) {
      this.trackCollapsed = collapsed;
      this.$nextTick(() => {
        setTimeout(() => this.mapScene && this.mapScene.map && this.mapScene.map.resize(), 300);
      });
    },

    // ==================== 左侧车辆列表（自有车 own_type=1 + 包车 own_type=3） ====================
    getVehicleList() {
      this.vehicleLoading = true;
      // TODO 对接后端（接口写好后替换下面一行即可）：
      this.$api.doAPI(API_VEHICLE_LIST, { page: 1, limit: 1000, own_type: [1, 3], text: this.keyword || undefined })
        .then((res) => {
          if (res.code === 0) {
            this.vehicles = res.data.list;
            // 页面首次加载默认选中列表第一辆（已选中车辆时不抢占，搜索也不影响当前选择）
            if (!this.current && this.vehicles.length) this.selectVehicle(this.vehicles[0]);
          }
        }).catch((err) => {
          console.error(err);
        }).finally(() => {
          this.vehicleLoading = false;
        });
    },

    selectVehicle(item) {
      this.current = item;
      this.activeTask = null;
      this.fetchTrack();
      this.fetchTaskList();
    },

    // 顶部查询按钮：按日期范围重新加载当前车辆轨迹 + 任务列表
    reloadTrack() {
      if (!this.current) return;
      this.activeTask = null;
      this.fetchTrack();
      this.fetchTaskList();
    },

    // ==================== 任务轨迹 ====================
    // 任务路单列表：当前车辆 + 日期范围
    fetchTaskList() {
      this.taskLoading = true;
      // TODO 对接后端（接口写好后替换下面 mock 即可）：
      // this.$api.doAPI(API_VEHICLE_TASKLIST, { vehicleid: this.current._id, vehicle_number: this.current.vehicle_number, start_day: this.start_day, end_day: this.end_day })
      // 约定返回 res.data.list：[{ _id, date, dispatch_no, route_no, line, vehicle_number, trailer_number, own_type, nodes }]
      new Promise((resolve) => {
        // mock 阶段：车辆列表来自真实接口，任务为模拟数据，按车牌号桥接匹配
        const v = (mockJson.data || []).find((n) => n.vehicle_number === this.current.vehicle_number) || {};
        const list = (v.tasks || []).filter((t) => t.date >= this.start_day && t.date <= this.end_day);
        setTimeout(() => resolve({ code: 0, data: { list } }), 150);
      }).then((res) => {
        this.taskList = res.data.list;
      }).catch((err) => {
        console.error(err);
      }).finally(() => {
        this.taskLoading = false;
      });
    },

    // 任务列表行配置：整行可点击选中任务
    taskRow(record) {
      return {
        on: { click: () => this.showTaskTrack(record) },
      };
    },

    // 查看任务轨迹：选中任务 → 显示流程图
    // TODO 若任务节点需按任务单独查询，在此处调 API_VEHICLE_TASKTRACK 拿 nodes 后再赋值
    showTaskTrack(record) {
      this.activeTask = record;
      this.$nextTick(() => {
        setTimeout(() => this.mapScene && this.mapScene.map && this.mapScene.map.resize(), 300);
      });
    },

    // 返回任务列表
    backToTaskList() {
      this.activeTask = null;
      this.$nextTick(() => {
        setTimeout(() => this.mapScene && this.mapScene.map && this.mapScene.map.resize(), 300);
      });
    },

    // 底部 tab 切换后重算地图尺寸
    onBottomTabChange() {
      this.$nextTick(() => {
        setTimeout(() => this.mapScene && this.mapScene.map && this.mapScene.map.resize(), 300);
      });
    },

    // 流程图相邻节点耗时（如 "3时18分" / "40分" / "58秒"），任一节点无时间则不显示
    flowDuration(t1, t2) {
      if (!t1 || !t2) return "";
      const ms = new Date(t2.replace(" ", "T")) - new Date(t1.replace(" ", "T"));
      if (isNaN(ms) || ms < 0) return "";
      const totalMin = Math.floor(ms / 60000);
      const sec = Math.round((ms % 60000) / 1000);
      if (totalMin <= 0) return `${sec}秒`;
      const h = Math.floor(totalMin / 60);
      const m = totalMin % 60;
      if (h <= 0) return `${m}分`;
      return m ? `${h}时${m}分` : `${h}时`;
    },

    // ==================== 地图初始化 ====================
    initMap() {
      const scene = new Scene({
        id: "carTrackMap",
        preserveDrawingBuffer: true,
        map: new GaodeMap({
          center: [113.625368, 34.7466], // 默认郑州
          style: "light",
          pitchEnable: false,
          token: "df486db18336b76e44d3d80a0cd23ce6",
          zoom: 11,
        }),
      });
      this.mapScene = scene;
      scene.on("loaded", () => {
        this.mapReady = true;
        // 注册起点旗帜、当前位置定位针图标
        scene.addImage("startIcon", START_ICON);
        scene.addImage("endIcon", END_ICON);
        if (this.trackPoints.length) this.renderTrack();
      });
    },

    // ==================== 轨迹数据 ====================
    fetchTrack() {
      this.trackLoading = true;
      // TODO 对接后端（接口写好后替换下面一行即可）：
      this.$api.doAPI(API_VEHICLE_TRACK, { vehicleid: this.current._id, vehicle_number: this.current.vehicle_number, start_day: this.start_day, end_day: this.end_day })
        // 约定返回 res.data.list：[{ longitude, latitude, time, address, speed }]
        .then((res) => {
          this.trackPoints = res.data.list || [];
          if (this.mapReady) this.renderTrack();
        }).catch((err) => {
          console.error(err);
        }).finally(() => {
          this.trackLoading = false;
        });
    },
    // 渲染轨迹线 + 起终点（带文字标注）
    renderTrack() {
      if (!this.mapReady) return;
      const scene = this.mapScene;
      [this.lineLayer, this.pointLayer, this.nameLayer, this.startNameLayer, this.endNameLayer, this.startLayer, this.endLayer].forEach((layer) => {
        if (layer) scene.removeLayer(layer);
      });

      const pts = this.trackPoints;
      const path = pts.map((p) => [p.longitude, p.latitude]);

      if (pts.length > 0) {
        // 轨迹线
        this.lineLayer = new LineLayer({ zIndex: 10 })
          .source([{ path }], { parser: { type: "json", coordinates: "path" } })
          .color("#1976d2")
          .size(5)
          .shape("line")
          .style({ opacity: 0.85 });
        scene.addLayer(this.lineLayer);

        // 途径点：蓝色小圆点（起点/当前位置处会被上层自定义图标盖住）
        this.pointLayer = new PointLayer({ zIndex: 11 })
          .source(pts, { parser: { type: "json", x: "longitude", y: "latitude" } })
          .shape("circle")
          .size(8)
          .color("#1976d2")
          .style({ stroke: "#ffffff", strokeWidth: 1.5, opacity: 0.9 });
        // scene.addLayer(this.pointLayer);
        // 途径点的名称（地址）标注，显示在小圆点上方
        // this.nameLayer = new PointLayer({ zIndex: 13 })
        //   .source(pts.slice(1, -1), { parser: { type: "json", x: "longitude", y: "latitude" } })
        //   .shape("address", "text")
        //   .size(12)
        //   .color("#1a2233")
        //   .style({
        //     textAnchor: "center",
        //     // L7 偏移按裁剪空间轴向：y 正值朝屏幕上方，名称显示在圆点上方
        //     textOffset: [0, 12],
        //     stroke: "#ffffff",
        //     strokeWidth: 3,
        //   });
        // scene.addLayer(this.nameLayer);
        // 起点名称：旗帜图标较大，需上移更多避免盖住图标
        this.startNameLayer = new PointLayer({ zIndex: 13 })
          .source([pts[0]], { parser: { type: "json", x: "longitude", y: "latitude" } })
          .shape("address", "text")
          .size(12)
          .color("#1a2233")
          .style({
            textAnchor: "center",
            // y 正值朝上：名称移到 28px 旗帜图标上方，不与图标重叠
            textOffset: [0, 24],
            stroke: "#ffffff",
            strokeWidth: 3,
          });
        scene.addLayer(this.startNameLayer);

        // 当前位置的名称显示在定位针下方，避免与针身重叠
        this.endNameLayer = new PointLayer({ zIndex: 13 })
          .source([pts[pts.length - 1]], { parser: { type: "json", x: "longitude", y: "latitude" } })
          .shape("address", "text")
          .size(12)
          .color("#1a2233")
          .style({
            textAnchor: "center",
            // y 负值朝下：名称移到定位针针尖下方，避开针身
            textOffset: [0, -16],
            stroke: "#ffffff",
            strokeWidth: 3,
          });
        scene.addLayer(this.endNameLayer);

        // 起点：绿色旗帜图标（与右上角图例一致）
        this.startLayer = new PointLayer({ zIndex: 12 })
          .source([pts[0]], { parser: { type: "json", x: "longitude", y: "latitude" } })
          .shape("startIcon")
          .size([28, 28]);
        scene.addLayer(this.startLayer);

        // 当前位置：红色定位针图标，y 正值朝上，上移图标让针尖对准坐标点
        this.endLayer = new PointLayer({ zIndex: 14 })
          .source([pts[pts.length - 1]], { parser: { type: "json", x: "longitude", y: "latitude" } })
          .shape("endIcon")
          .size([30, 30])
          .style({ offsets: [0, 12] });
        scene.addLayer(this.endLayer);

        this.fitBounds(path);
      }
    },

    // 根据轨迹边界自动调整中心与缩放级别
    fitBounds(path) {
      const lngs = path.map((p) => p[0]);
      const lats = path.map((p) => p[1]);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      this.mapScene.setCenter([(minLng + maxLng) / 2, (minLat + maxLat) / 2]);
      const d = Math.max(maxLng - minLng, maxLat - minLat);
      let zoom = 15;
      if (d > 0.5) zoom = 8;
      else if (d > 0.2) zoom = 10;
      else if (d > 0.1) zoom = 11;
      else if (d > 0.05) zoom = 12;
      else if (d > 0.02) zoom = 13;
      else if (d > 0.01) zoom = 14;
      this.mapScene.setZoom(zoom);
    },
  },
};
</script>

<style lang="less" scoped>
// 整页高度：100vh 减去顶部导航(64)与 v-main 底部留白(15)
.car-page {
  padding: 8px;
  max-width: 100%;
  height: calc(100vh - 79px);
}

.car-row {
  height: 100%;
}

.left-col,
.right-col {
  height: 100%;
}

// ==================== 左侧车辆列表 ====================
.left-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-right: 8px;
}

.panel-header {
  flex: 0 0 auto;
  padding: 8px;
  border-bottom: 1px solid #f0f2f5;

  .search-input {
    margin: 0;
  }
}

.vehicle-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.vehicle-item {
  padding: 10px 14px;
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #f5f8ff;
  }

  &.active {
    background: #e8f1ff;

    .vehicle-no {
      color: #1976d2;
    }
  }

  .vehicle-no {
    font-size: 14px;
    font-weight: 600;
    color: #1a2233;
  }

  .vehicle-meta {
    margin-top: 3px;
    font-size: 12px;
    color: #86909c;

    .v-own {
      margin-left: 8px;
      padding: 0 6px;
      border-radius: 3px;
    }

    .own-self {
      color: #1976d2;
      background: #e8f1ff;
    }

    .own-charter {
      color: #ff7d00;
      background: #fff3e8;
    }
  }
}

.empty-tip {
  padding: 40px 0;
  text-align: center;
  color: #c0c4cc;
  font-size: 13px;
}

.panel-footer {
  flex: 0 0 auto;
  padding: 8px;
  font-size: 12px;
  color: #86909c;
  text-align: center;
  border-top: 1px solid #f0f2f5;
}

// ==================== 右侧区域 ====================
.right-wrap {
  display: flex;
  flex-direction: column;
  height: 100%;

  >*+* {
    margin-top: 8px;
  }
}

.toolbar-card {
  flex: 0 0 auto;
  padding: 8px 12px;

  .date-field {
    width: 150px;
  }

  .date-sep {
    margin: 0 8px;
    color: #86909c;
  }

  .current-vehicle {
    display: inline-flex;
    align-items: center;
    font-size: 14px;
    font-weight: 600;
    color: #1976d2;
  }

  .track-legend {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    color: #4e5969;

    .legend-icon {
      margin-right: 3px;
      vertical-align: middle;
    }

    em {
      font-style: normal;
      color: #86909c;
    }
  }
}

// 地图：占满右侧剩余高度
.map-card {
  position: relative;
  flex: 1 1 auto;
  min-height: 200px;
  border: 1px solid #dfe4ed;
  border-radius: 2px;
  overflow: hidden;
  background: #f2f3f5;

  .map-box {
    width: 100%;
    height: 100%;
  }
}

.map-mask {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #86909c;
  background: rgba(255, 255, 255, 0.7);

  p {
    margin: 8px 0 0;
    font-size: 13px;
  }
}

// 下方轨迹明细：固定高度，表头吸顶、表体滚动
.track-card {
  flex: 0 0 auto;

  .track-title {
    display: flex;
    align-items: center;
    padding: 0 12px;
    font-size: 13px;
    font-weight: 600;
    color: #1a2233;
    border-bottom: 1px solid #f0f2f5;

    .track-count {
      margin-left: 2px;
      font-weight: 400;
      font-size: 12px;
      color: #86909c;
    }

    // 底部 tab：任务轨迹 / 轨迹明细，压缩上下留白
    .bottom-tabs {
      margin-left: 12px;

      ::v-deep .ant-tabs-bar {
        margin-bottom: 0;
        border-bottom: none;
      }

      ::v-deep .ant-tabs-tab {
        padding: 8px 12px;
        margin-right: 4px;
        font-size: 13px;
      }
    }
  }
}

// ==================== 任务轨迹 ====================
// 任务列表：整行可点击
.task-pane {
  ::v-deep .ant-table-tbody>tr {
    cursor: pointer;

    &:hover>td {
      background: #f5f8ff;
    }
  }

  .v-own {
    padding: 0 6px;
    border-radius: 3px;
    font-size: 12px;
  }

  .own-self {
    color: #1976d2;
    background: #e8f1ff;
  }

  .own-charter {
    color: #ff7d00;
    background: #fff3e8;
  }
}

// 流程图头部（返回 + 当前任务信息）
.task-flow-head {
  display: flex;
  align-items: center;
  padding: 4px 12px;
  border-bottom: 1px solid #f0f2f5;

  .back-btn {
    min-width: auto;
  }

  .task-flow-title {
    margin-left: 12px;
    font-size: 13px;
    font-weight: 600;
    color: #1a2233;

    em {
      margin-left: 10px;
      font-style: normal;
      font-weight: 400;
      font-size: 12px;
      color: #86909c;
    }
  }
}

// 流程图：a-steps 两行蛇形步骤条，节点均分、连线自适应，铺满整行不出现横向滚动
.task-flow {
  padding: 6px 16px 12px 32px; // 左侧留 16px，避免行首节点贴边
  overflow-x: hidden;
}

.steps-row {
  position: relative; // 耗时标签绝对定位基准

  &.reverse-row {
    display: flex;
    justify-content: flex-end; // 第二行右对齐，出围栏落在封车正下方
  }
}

// 自定义节点圆：时间两行 / 未完成灰色空心 --
.step-dot {
  width: 52px;
  height: 52px;
  margin: 0 auto;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  line-height: 1.25;
  border: 2px solid #c9cdd4;
  color: #86909c;
  background: #fff;

  &.done {
    border-color: #165dff;
    background: #165dff;
    color: #fff;
  }

  .node-empty {
    color: #c9cdd4;
    font-size: 14px;
  }
}

// 连线耗时标签：两节点连线中点正上方
.line-duration {
  position: absolute;
  top: 8px; // 圆心(26px)上方
  transform: translateX(-50%);
  font-size: 12px;
  color: #86909c;
  white-space: nowrap;
  pointer-events: none;
}

// 第一行节点名在圆上方，耗时标签挪到节点名与连线之间
.steps-row:not(.reverse-row) .line-duration {
  top: 30px;
}

// 跨行向下竖线：右端对齐行尾节点圆心（容器宽 1/12 处）
.turn-wrap {
  position: relative;
  height: 30px;

  .turn-line {
    position: absolute;
    top: 0;
    bottom: 0;
    right: calc(100% / 12 - 1px); // 行尾节点圆心
    width: 2px;
    background: #e5e6eb;

    &.active {
      background: #165dff;
    }

    &.empty {
      background: #f0f2f5;
    }
  }

  em {
    position: absolute;
    right: calc(100% / 12 + 8px);
    top: 50%;
    transform: translateY(-50%);
    font-style: normal;
    font-size: 12px;
    color: #86909c;
    white-space: nowrap;
  }
}

// a-steps 覆盖：icon 换成 52px 圆，连线对齐圆心，节点名下方居中
.task-flow ::v-deep {
  .ant-steps-item {
    flex: 1 1 0 !important; // 强制均分整行
    min-width: 0 !important;
    width: auto !important;
    margin-right: 0 !important;
    padding-bottom: 2px;
  }

  .ant-steps-item-container {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .ant-steps-item-icon {
    float: none !important;
    width: 52px;
    height: 52px;
    margin: 0 !important; // 清掉 label-vertical 默认 margin-left 偏移
    border: none;
    background: transparent;
    line-height: normal;
  }

  // 连线：穿过圆心，从本圆右缘到下一圆左缘
  // tail 相对整个 item 定位，item 中心 = 圆心（align-items:center）
  .ant-steps-item-tail {
    top: 26px; // 第二行（icon 在上）：圆心距顶部 = 52/2
    left: calc(50% + 30px); // item 中心 + 圆半径 26 + 间隙 4
    width: calc(100% - 60px); // 到下一 item 中心 - 圆半径 26 - 间隙 4（item 宽 = 圆心间距）
    margin: 0;
    margin-top: -1px; // 线高 2px 的一半，垂直居中
    padding: 0;

    &::after {
      height: 2px;
      background: #e5e6eb;
    }
  }

  // 完成节点连线蓝色（wait 保持灰）
  .ant-steps-item-finish .ant-steps-item-tail::after {
    background: #165dff;
  }

  .ant-steps-item-content {
    width: 100%;
    text-align: center;
  }

  .ant-steps-item-title {
    margin-top: 6px;
    padding-right: 0;
    font-size: 12px;
    color: #1a2233;
    white-space: nowrap;

    &::after {
      display: none; // 关掉 title 自带连线，统一用 tail
    }
  }
}

// 第一行：节点名放圆上方（column-reverse 交换 title/icon），连线/耗时随之下移
.steps-row:not(.reverse-row) ::v-deep {
  .ant-steps-item-container {
    flex-direction: column-reverse;
  }

  .ant-steps-item-title {
    margin-top: 0;
    margin-bottom: 6px;
  }

  // icon 在下，圆心距 item 顶部 = content(30px) + margin-bottom(6px) + 26px + 余量 ≈ 65px
  .ant-steps-item-tail {
    top: 65px;
  }
}

// 明细标题栏右侧折叠按钮（与 map.vue 样式统一）
.collapse-btn {
  min-width: auto;
  color: #1976d2 !important;
  font-weight: 600;

  ::v-deep .v-icon {
    color: #1976d2;
  }
}

// 折叠后地图左下角的展开入口（与 map.vue 样式统一）
.list-expand-btn {
  position: absolute;
  left: 12px;
  bottom: 35px;
  z-index: 20;
  display: flex;
  align-items: center;
  height: 30px;
  padding: 0 12px;
  font-size: 13px;
  font-weight: 600;
  color: #1976d2;
  background: #fff;
  border: 1px solid #9dc1fb;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.15);
  cursor: pointer;
  user-select: none;
  transition: background 0.2s;

  &:hover {
    background: #e8f1ff;
  }
}
</style>
